import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { AzureService } from 'src/app/core/auth/azure.service'
import { User } from 'src/app/shared/models/user.model'
import { FormsModule } from '@angular/forms'
import { DropdownModule } from 'primeng/dropdown'
import { MultiSelectModule } from 'primeng/multiselect'
import { SliderModule } from 'primeng/slider'
import { CheckboxModule } from 'primeng/checkbox'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { InputNumberModule } from 'primeng/inputnumber'
import { ButtonModule } from 'primeng/button'
import mappingGroups from './MappingGroups.json'
import { MappingGroup } from 'src/app/core/auth/azure.service'
import { SettingsService } from './services/settings.service'
import { MessageService } from 'primeng/api'
import { InputTextModule } from 'primeng/inputtext'

export interface SettingsRule extends Rule {
  isEditing?: boolean
  isExpanded?: boolean
}

export interface Rule {
  ruleId?: string // Optional until received from backend
  capType: string
  thresholdPct: number
  capPct: number | null
  capPctRange: [number | null, number | null] | []
  customers: any[]
  vertical1?: string | null
  vertical2?: string | null
  priceRange: [number | null, number | null]
  priority: number
  active: boolean
  updatedAt?: number // Optional until received from backend
}

@Component({
  selector: 'app-settings',
  template: `
    <div class="submitButtons">
      <p-button
        [outlined]="true"
        (click)="addRule()"
        label="Add Rule"
        icon="pi pi-plus" />
      <p-button
        *ngIf="rules.length > 0"
        (click)="submitRules()"
        label="Submit Rules"
        icon="pi pi-check" />
    </div>

    <div class="settingsContainer">
      <div class="rulesEditor">
        <div
          *ngFor="let rule of rules.slice().reverse(); let i = index"
          class="rulesContainer">
          <h4 (click)="toggleExpand(rule)" class="rulesContainer__header">
  <span class="rulesContainer__dot"></span>
  <span class="rulesContainer__title">
    Rule {{ rules.length - i }} - {{ rule.capType }}
  </span>

  <span class="rulesContainer__right">
    <span
      class="rulesContainer__status"
      [class.actives]="rule.active"
      [class.inactives]="!rule.active">
      {{ rule.active ? 'Active' : 'Inactive' }}
    </span>
    <span
      class="rulesContainer__expand"
      [class.expanded]="rule.isExpanded">
      <i class="pi pi-chevron-down"></i>
    </span>
  </span>
</h4>

      <div class="ruleContainer" [class.hidden]="!rule.isExpanded">
        <div class="ruleContainer__content">
          <!-- Left side: Descriptions -->
          <div class="ruleContainer__descriptions">
            <div class="descriptionGroup" >
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Cap Type</label>
                <p class="text-small-roman">Select whether this is a hard or soft cap rule</p>
              </div>
              <div class="descriptionItem" *ngIf="rule.capType === 'hard'">
                <label class="text-caption-strong-bold">Cap Percentage</label>
                <p class="text-small-roman">The maximum percentage cap for limiting the price percentage increase</p>
              </div>
              <div class="descriptionItem" *ngIf="rule.capType === 'soft'">
                <label class="text-caption-strong-bold">Cap Percentage Range</label>
                <p class="text-small-roman">The range for limiting the price percentage increase in soft cap rules</p>
              </div>
            </div>
            <div class="descriptionGroup">
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Category</label>
                <p class="text-small-roman">Select a category to filter customers, or leave empty for all</p>
              </div>
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Subcategory</label>
                <p class="text-small-roman">Select a subcategory to further filter customers, or leave empty for all</p>
              </div>
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Customers</label>
                <p class="text-small-roman">Select specific customers this rule applies to, or leave empty for all</p>
              </div>
            </div>
            <div class="descriptionGroup">
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Threshold Percentage</label>
                <p class="text-small-roman">Apply cap only when the algorithm decides the price is greater than this percentage threshold</p>
              </div>
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Priority</label>
                <p class="text-small-roman">Lower values have higher priority. Rules are evaluated in order</p>
              </div>
              <div class="descriptionItem">
                <label class="text-caption-strong-bold">Price Range</label>
                <p class="text-small-roman">The price range this rule applies to</p>
              </div>
            </div>
          </div>

          <!-- Right side: Inputs -->
          <div class="ruleContainer__inputs">
            <div class="inputGroup">
              <p-dropdown
                class="inputs"
                [options]="['hard', 'soft']"
                [(ngModel)]="rule.capType"
                placeholder="Select cap type"
                [disabled]="!rule.isEditing" />
            </div>
            <div class="inputGroup" *ngIf="rule.capType === 'hard'">
              <p-inputNumber
                class="inputs"
                type="number"
                [(ngModel)]="rule.capPct"
                placeholder="Cap Percentage"
                [disabled]="!rule.isEditing" />
            </div>
            <div class="inputGroup inputItem--range" *ngIf="rule.capType === 'soft'">
              <div class="inputItem__rangeInputs">
                <p-inputNumber
                  class="inputs inputs--range"
                  type="number"
                  [(ngModel)]="rule.capPctRange[0]"
                  placeholder="Min"
                  [disabled]="!rule.isEditing" />
                <span class="inputItem__separator">-</span>
                <p-inputNumber
                  class="inputs inputs--range"
                  type="number"
                  [(ngModel)]="rule.capPctRange[1]"
                  placeholder="Max"
                  [disabled]="!rule.isEditing" />
              </div>
            </div>
            <div class="dividerLine"></div>           
            <div class="inputGroup">              
            <p-dropdown
                class="inputs"
                [options]="vertical1Options"
                [(ngModel)]="rule.vertical1"
                [disabled]="!rule.isEditing"
                placeholder="Select Category"
                [showClear]="true"
                />
            </div>
            <div class="inputGroup">              
            <p-dropdown
                class="inputs"
                [options]="vertical2Options"
                [(ngModel)]="rule.vertical2"
                [disabled]="!rule.isEditing"
                placeholder="Select Subcategory"
                [showClear]="true"
                />
            </div>
            <div class="inputGroup">              
            <p-multiSelect
                class="inputs"
                [options]="customers"
                [(ngModel)]="rule.customers"
                [disabled]="!rule.isEditing"
                placeholder="Select Customers"
                optionLabel="name"
                />
            </div>
            <div class="dividerLine"></div>
            <div class="inputGroup">
              <p-inputNumber
                class="inputs"
                type="number"
                [(ngModel)]="rule.thresholdPct"
                placeholder="Threshold Percentage"
                [disabled]="!rule.isEditing" />
            </div>
            <div class="inputGroup">
              <p-inputNumber
                class="inputs"
                type="number"
                [(ngModel)]="rule.priority"
                placeholder="Priority"
                [disabled]="!rule.isEditing" />
            </div>
            <div class="inputGroup inputItem--range">
              <div class="inputItem__rangeInputs">
                <p-inputNumber
                  class="inputs inputs--range"
                  type="number"
                  [(ngModel)]="rule.priceRange[0]"
                  placeholder="Min"
                  [disabled]="!rule.isEditing || rule.capType !== 'soft'" />
                <span class="inputItem__separator">-</span>
                <p-inputNumber
                  class="inputs inputs--range"
                  type="number"
                  [(ngModel)]="rule.priceRange[1]"
                  placeholder="Max"
                  [disabled]="!rule.isEditing || rule.capType !== 'soft'" />
              </div>
            </div>
          </div>
        </div>

        <div class="ruleContainer__footer">
          <div class="ruleContainer__buttons">
            <p-button
              (click)="toggleEdit(rule)"
              [icon]="rule.isEditing ? 'pi pi-check' : 'pi pi-pencil'"
              [label]="rule.isEditing ? 'Save' : 'Edit'"
              size="small">
            </p-button>
            <p-button
              (click)="removeRule(i)"
              label="Remove"
              icon="pi pi-times"
              size="small" />
            <p-checkbox
              [(ngModel)]="rule.active"
              [binary]="true"
              label="Active"
              (onChange)="submitRules()" />
          </div>
        </div>
      </div>
      <hr />
        </div>
      </div>

      <div class="rulesPriority">
        <h3>Rules by Priority</h3>
        <div *ngIf="rules.length === 0" class="emptyState">
          <p>No rules configured</p>
        </div>
        <div *ngIf="rules.length > 0" class="priorityWaterfall">
          <div
            *ngFor="let rule of sortedRules; let i = index"
            class="priorityItem"
            [class.inactive]="!rule.active">
            <div class="priorityItem__header">
              <span class="priorityItem__priority">{{ rule.priority }}</span>
              <span class="priorityItem__title">
                {{ rule.capType | uppercase }} Cap
              </span>
              <span class="priorityItem__status" [class.active]="rule.active">
                {{ rule.active ? 'ACTIVE' : 'INACTIVE' }}
              </span>
            </div>
            <div class="priorityItem__details">
              <div class="priorityItem__detail">
                <span class="label">Threshold:</span>
                <span class="value">{{ rule.thresholdPct }}%</span>
              </div>
              <div class="priorityItem__detail">
                <span class="label">Customers:</span>
                <span class="value">{{ rule.customers.length > 0 ? rule.customers.length + ' customers' : 'All' }}</span>
              </div>
              <div class="priorityItem__detail" *ngIf="rule.vertical1">
                <span class="label">Category:</span>
                <span class="value">{{ rule.vertical1 }}</span>
              </div>
              <div class="priorityItem__detail" *ngIf="rule.vertical2">
                <span class="label">Subcategory:</span>
                <span class="value">{{ rule.vertical2 }}</span>
              </div>
              <div class="priorityItem__detail" *ngIf="rule.capType === 'hard'">
                <span class="label">Cap:</span>
                <span class="value">{{ rule.capPct }}%</span>
              </div>
              <div class="priorityItem__detail" *ngIf="rule.capType === 'soft'">
                <span class="label">Cap Range:</span>
                <span class="value">{{ rule.capPctRange[0] }}% - {{ rule.capPctRange[1] }}%</span>
              </div>
              <div class="priorityItem__detail" *ngIf="rule.capType === 'soft'">
                <span class="label">Price Range:</span>
                <span class="value">{{ rule.priceRange[0] }} - {{ rule.priceRange[1] }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .inputGroup {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 440px;

        &__checkbox {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        &__slider {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }
      }

      .inputs {
        display: contents;
      }
      .p-dropdown {
        width: 100px !important;
      }
      .settingsContainer {
        display: grid;
        grid-template-columns: 1fr 320px;
        gap: 20px;
        padding: 0 1rem;
        height: calc(100vh - 200px);
        overflow: hidden;
      }

      .rulesEditor {
        overflow-y: auto;
        padding-right: 10px;

        &::-webkit-scrollbar {
          width: 8px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 4px;

          &:hover {
            background: #999;
          }
        }
      }

      .rulesContainer {
        display: flex;
        flex-direction: column;
        padding-inline: 1rem;


  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__status {
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 16px;
    background: #ffebee;
    color: #c62828;

    &.actives {
      background: #e8f5e9;
      color: #2e7d32;
    }

    &.inactives {
      background: #ffebee;
      color: #c62828;
    }
  }

        &__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          user-select: none;
          margin: 1rem 0 0.5rem 0;
          padding: 10px;
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        &__dot {
          width: 12px;
          height: 12px;
          background-color: #883cae;
          border-radius: 50%;
          flex-shrink: 0;
          margin-right: 8px;
        }

        &__title {
          flex: 1;
        }

        &__expand {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease;
          color: #666;

          &.expanded {
            transform: rotate(180deg);
          }
        }
      }

      .ruleContainer {
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding-inline: 1rem;
        transition: all 0.3s ease;

        &.hidden {
          display: none;
        }

        &__content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          padding: 1rem 0;
        }

        &__descriptions {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        &__inputs {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        &__footer {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        &__buttons {
          display: flex;
          gap: 15px;
          align-items: center;
        }
      }

      .descriptionItem {
        display: flex;
        flex-direction: column;
        gap: 4px;

        label {
          font-size: 14px;
        }

        p {
          margin: 0;
          color: #666;
          font-size: 12px;
          line-height: 1.4;
        }
      }

      .descriptionGroup {
        display: flex;
        flex-direction: column;
        gap: 20px;

        &:not(:first-child) {
          margin-top: 2rem;
        }
      }

      .dividerLine {
        height: 1px;
        background: linear-gradient(to right, transparent, #ddd 20%, #ddd 20%, transparent);
        margin: 8px 0;
        margin-left: -5rem;
      }

      .inputItem {
        display: flex;
        flex-direction: column;
        gap: 4px;

        &--range {
          gap: 8px;
        }

        &__slider {
          display: flex;
          justify-content: space-between;
          padding-top: 4px;
          font-size: 12px;
          color: #666;
        }

        &__rangeInputs {
          display: flex;
          gap: 6px;
          align-items: center;

          .inputs--range {
            flex: 1;
            width: 70px;
          }
        }

        &__separator {
          font-size: 14px;
          color: #666;
          font-weight: 500;
          line-height: 1;
        }

        &__unit {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }
      }

      .submitButtons {
        display: flex;
        gap: 15px;
        padding: 1rem;
        margin-bottom: 1rem;
        justify-content: space-between;
      }

      .rulesPriority {
        background: white;
        border: 1px solid #ddd;
        padding: 1.5rem;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        h3 {
          margin: 0 0 1.5rem 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
          font-family: "Siemens Sans", sans-serif;
        }
      }

      .emptyState {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #999;
        font-size: 12px;
      }

      .priorityWaterfall {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .priorityItem {
        background: #f8f9fa;
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        padding: 12px;
        font-size: 12px;
        transition: all 0.2s ease;
        font-family: "Siemens Sans", sans-serif;


        &.inactive {
          opacity: 0.7;
          background: #f5f5f5;
        }

        &__header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e8e8e8;
        }
  

        &__priority {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #883cae;
          color: white;
          border-radius: 50%;
          font-weight: 600;
          font-size: 12px;
          flex-shrink: 0;
        }

        &__title {
          flex: 1;
          font-weight: 600;
          color: #333;
        }

        &__status {
          padding: 3px 8px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 10px;
          background: #ffebee;
          color: #c62828;

          &.active {
            background: #e8f5e9;
            color: #2e7d32;
            border-bottom: none !important;
          }
        }

        &__details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        &__detail {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 0;

          .label {
            font-weight: 500;
            color: #666;
          }

          .value {
            color: #333;
            font-weight: 600;
          }
        }
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    MultiSelectModule,
    SliderModule,
    CheckboxModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    ButtonModule,
    InputTextModule,
  ],
})
export class Settings implements OnInit {
  private azureService = inject(AzureService)
  private settingsService = inject(SettingsService)
  private messageService = inject(MessageService)

  private readonly _destroying$ = new Subject<void>()

  currentUser: User | null = null
  userGroups: { label: string; value: string }[] = []
  selectedBusinessArea: string = ''
  mappingGroups: MappingGroup[] = mappingGroups
  rules: SettingsRule[] = []
  customers: any[] = []
  vertical1Options: { label: string; value: string }[] = []
  vertical2Options: { label: string; value: string }[] = []

  get sortedRules(): SettingsRule[] {
    return [...this.rules].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority
      }
      return this.rules.indexOf(a) - this.rules.indexOf(b)
    })
  }

  constructor() {
    this.azureService
      .getCurrentUser()
      .pipe(takeUntil(this._destroying$))
      .subscribe((userData) => {
        this.currentUser = userData
      })

      this.azureService.selectedBusinessArea.subscribe((businessArea) => {
        this.selectedBusinessArea = businessArea;
        this.getRules();
      });
  
    this.loadCustomers()
  }

  ngOnInit(): void {
    this.getRules()
  }

  getRules() {
    this.settingsService.getRules(this.selectedBusinessArea).subscribe({
      next: (response) => {
        console.log('Rules:', response)
        this.rules = response.rules
      },
      error: (error) => {
        console.error('Error getting rules:', error)
      },
    })
  }


  loadCustomers(): void {
    this.settingsService.getCustomers(this.selectedBusinessArea).subscribe({
      next: (response) => {
        console.log('Customers:', response)
        this.customers = response.customers
        
        const vertical1Set = new Set<string>()
        const vertical2Set = new Set<string>()
        
        response.customers.forEach((customer: any) => {
          if (customer.vertical1) vertical1Set.add(customer.vertical1)
          if (customer.vertical2) vertical2Set.add(customer.vertical2)
        })
        
        this.vertical1Options = Array.from(vertical1Set).map((v) => ({
          label: v,
          value: v,
        }))
        
        this.vertical2Options = Array.from(vertical2Set).map((v) => ({
          label: v,
          value: v,
        }))
      },
      error: (error) => {
        console.error('Error getting customers:', error)
      },
    })
  }

  /*  validateCapPctRange(rule: Rule): void {
    if (rule.capPctRange[0] !== null && rule.capPctRange[1] !== null) {
      if (rule.capPctRange[0] > rule.capPctRange[1]) {
        // If the minimum is greater than the maximum, reset the maximum
        rule.capPctRange[1] = rule.capPctRange[0]
      }
    }
  } */

  addRule(): void {
    const newRule: SettingsRule = {
      capType: 'soft',
      thresholdPct: 15,
      capPct: null,
      capPctRange: [10, 15],
      customers: [],
      vertical1: null,
      vertical2: null,
      priceRange: [null, null],
      priority: 1,
      active: true,
      isEditing: true,
      isExpanded: true,
    }
    this.rules.push(newRule)
  }

  removeRule(index: number): void {
    this.rules.splice(this.rules.length - index - 1, 1)
    this.submitRules('remove')
  }

  toggleEdit(rule: SettingsRule): void {
    if (rule.isEditing) {
      console.log('Saving rule:', rule)
    }
    rule.isEditing = !rule.isEditing
  }

  toggleExpand(rule: SettingsRule): void {
    rule.isExpanded = !rule.isExpanded
  }

  submitRules(action?: string): void {
    this.rules.forEach((rule) => {
      rule.isEditing = false // Disable editing state
    })

    if (!this.validateRules()) return
    const submittedRules = this.rules.map(({ isEditing, ...rule }) => rule)
    this.settingsService
      .createRules(this.selectedBusinessArea, submittedRules)
      .subscribe({
        next: (response) => {
          console.log('Response:', response)
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: action === 'remove' ? 'Rule removed!' : 'Rules changed!',
          })
          this.rules = response.rules
        },
        error: (error) => {
          console.error('Error submitting rules:', error)
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'There is some error while processing your request.',
          })
        },
      })

    console.log('Submitting Rules:', this.rules)
  }

  validateRules(): boolean {
    if (this.rules.length === 0) return true
    return this.rules.every((rule) => {
      if (rule.capType === 'hard') {
        if (rule.capPct === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Cap Percentage is required for hard cap type.',
          })
          return false
        } else {
          rule.priceRange = [null, null]
          rule.capPctRange = []
        }
      } else {
        if (rule.capPctRange[0] === null || rule.capPctRange[1] === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Cap Percentage Range is required for soft cap type.',
          })
          return false
        }
        rule.capPct = null
      }
      return true
    })
  }
}
